import {
    CollisionDetection,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    MeasuringStrategy,
    Modifier,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    restrictToHorizontalAxis,
    restrictToParentElement,
    restrictToVerticalAxis,
    restrictToWindowEdges,
} from '@dnd-kit/modifiers';
import {
    arrayMove,
    horizontalListSortingStrategy,
    rectSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    SortingStrategy,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {ReactNode, useCallback} from 'react';

import pointerWithinVertical from './utils';

type ContainerSortingType = 'grid' | 'horizontal' | 'vertical';
type StrategyMapType = {
    collisionDetection?: CollisionDetection;
    modifiers: Modifier[];
    strategy: SortingStrategy;
};
const StrategyMap: Record<ContainerSortingType, StrategyMapType> = {
    grid: {
        collisionDetection: undefined,
        modifiers: [restrictToWindowEdges],
        strategy: rectSortingStrategy,
    },
    horizontal: {
        collisionDetection: undefined,
        modifiers: [restrictToHorizontalAxis],
        strategy: horizontalListSortingStrategy,
    },
    vertical: {
        collisionDetection: pointerWithinVertical,
        modifiers: [restrictToVerticalAxis],
        strategy: verticalListSortingStrategy,
    },
};

type SortableVerticalProps<T extends {id: string}> = {
    items: T[];
    onSortChange?: (items: T[]) => void;
    renderItem: (item: T, index: number) => ReactNode;
    sortingStrategy?: ContainerSortingType;
};

const SortableContainer = <T extends {id: string}>({
    items,
    onSortChange,
    renderItem,
    sortingStrategy = 'vertical',
}: SortableVerticalProps<T>) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(TouchSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            const {active, over} = event;

            if (over === null) {
                return;
            }

            if (active.id !== over.id) {
                const oldIndex = items.findIndex(
                    (item) => item.id === active.id,
                );
                const newIndex = items.findIndex((item) => item.id === over.id);

                if (onSortChange) {
                    onSortChange(arrayMove(items, oldIndex, newIndex));
                }
            }
        },
        [items, onSortChange],
    );

    const {collisionDetection, modifiers, strategy} =
        StrategyMap[sortingStrategy];

    return (
        <DndContext
            collisionDetection={collisionDetection}
            measuring={{droppable: {strategy: MeasuringStrategy.Always}}}
            modifiers={[...modifiers, restrictToParentElement]}
            onDragEnd={handleDragEnd}
            sensors={sensors}
        >
            <SortableContext items={items} strategy={strategy}>
                {items.map(renderItem)}
            </SortableContext>
        </DndContext>
    );
};

export default SortableContainer;
